import * as fs from "node:fs";
import * as path from "node:path";
import { parse as parseYaml } from "yaml";
import type { TokenCredential } from "@azure/identity";
import type { ReasoningEffort } from "@azure/ai-projects";
import { renderPersonaBody } from "./persona-render.js";
import { createPromptVersion } from "./foundry-agents.js";
import { LocalCliError } from "./errors.js";
import { productVisibilityFor } from "@m8t-stack/api-contract";

interface AdvisorFrontmatter {
  version?: string | number;
  targets?: {
    foundry?: {
      model?: string;
      reasoning?: { effort?: string };
      /** Forwarded to Foundry VERBATIM — see the note in deployPromptAdvisor. */
      tools?: unknown;
    };
  };
  "fillable-fields"?: { name: string; default?: string }[];
}

/**
 * Resolve the personas directory root starting from `hint`, walking up until a
 * `personas/` directory is found. This allows the function to be called with
 * `process.cwd()` even when the cwd is a subdirectory of the repo checkout
 * (e.g. `apps/cli/` in the monorepo layout).
 *
 * Returns `null` when neither `hint` nor any ancestor contains a `personas/` dir.
 */
function findPersonasRoot(hint: string): string | null {
  let dir = path.resolve(hint);
  const fsRoot = path.parse(dir).root;
  // Walk up from `dir` toward the filesystem root, checking each level.
  for (;;) {
    if (fs.existsSync(path.join(dir, "personas"))) return dir;
    const parent = path.dirname(dir);
    if (parent === dir || dir === fsRoot) return null;
    dir = parent;
  }
}

/**
 * Deploy an advisor persona as a Foundry prompt agent.
 *
 * Reads model + reasoning from `targets.foundry.*` in the persona frontmatter,
 * builds the instructions via `renderPersonaBody` (substituting any
 * `fillable-fields` defaults so no `{{placeholder}}` residue is left), then
 * calls `createPromptVersion` under the given `agentName`.
 *
 * The `repoRoot` may be the exact checkout root or any subdirectory — the function
 * walks up to locate the `personas/` directory. This lets callers pass
 * `process.cwd()` safely regardless of which subdirectory they are in.
 *
 * The onboarding intake persona declares fillable-fields with graceful defaults, so a
 * caller that supplies no overrides still renders a placeholder-free body.
 */
export async function deployPromptAdvisor(args: {
  credential: TokenCredential;
  endpoint: string;
  repoRoot: string;
  persona: string;
  agentName: string;
  /** Override the model from frontmatter (optional). */
  model?: string;
  /** Override specific fillable-field values; merged over frontmatter defaults (caller wins). */
  fieldOverrides?: Record<string, string>;
  /** Human-facing progress channel; forwarded to createPromptVersion. Pass only in
   *  human output modes (machine-parsed streams must stay clean). */
  onNotice?: (msg: string) => void;
}): Promise<string> {
  const resolvedRoot = findPersonasRoot(args.repoRoot);
  const personaPath = resolvedRoot
    ? path.join(resolvedRoot, "personas", args.persona, "persona.md")
    : path.join(args.repoRoot, "personas", args.persona, "persona.md");

  let raw: string;
  try {
    raw = fs.readFileSync(personaPath, "utf8");
  } catch (e) {
    throw new LocalCliError({
      code: "ADVISOR_PERSONA_MISSING",
      message: `Could not read the persona at '${personaPath}'.`,
      hint: "Run from your m8t repo checkout, or pass --repo-root.",
      cause: e,
    });
  }

  // \r?\n tolerates CRLF line endings (Windows git checkouts) as well as LF.
  const fmMatch = /^---\r?\n([\s\S]*?)\r?\n---/.exec(raw);
  const fm: AdvisorFrontmatter = fmMatch ? (parseYaml(fmMatch[1]) as AdvisorFrontmatter) : {};

  const model = args.model ?? fm.targets?.foundry?.model;
  if (!model) {
    throw new LocalCliError({
      code: "ADVISOR_NO_MODEL",
      message: `Persona '${args.persona}' has no targets.foundry.model.`,
      hint: `Set targets.foundry.model in personas/${args.persona}/persona.md.`,
    });
  }

  const VALID_EFFORTS: ReadonlySet<string> = new Set([
    "none",
    "minimal",
    "low",
    "medium",
    "high",
    "xhigh",
  ]);
  const rawEffort = fm.targets?.foundry?.reasoning?.effort;
  if (rawEffort !== undefined && !VALID_EFFORTS.has(rawEffort)) {
    throw new LocalCliError({
      code: "ADVISOR_BAD_EFFORT",
      message: `Invalid reasoning.effort '${rawEffort}' in persona '${args.persona}'. Valid values: none, minimal, low, medium, high, xhigh.`,
    });
  }
  const reasoningEffort = rawEffort as ReasoningEffort | undefined;

  // Persona-declared tools, forwarded VERBATIM. The persona file is the declaration
  // (documented in docs/architecture/SCHEMA.md); this function is only the courier, so a
  // new Foundry tool type needs no change here.
  //
  // This used to be dropped on the floor: the frontmatter declared tools, nothing read
  // them, and a freshly installed advisor came up with only what `brain link` and
  // `a2a enable` append afterwards. The Ezra persona's grounding rule instructs it to
  // "call the Microsoft Learn MCP first" — a tool the deploy never granted. A capability
  // the instructions BELIEVE IN but the deploy withholds is worse than an absent one: the
  // agent reports it cannot find a tool it was told it has.
  //
  // Fails closed on a malformed declaration rather than deploying a silently tool-less
  // agent, which is the failure mode that hid this for so long.
  const rawTools = fm.targets?.foundry?.tools;
  if (rawTools !== undefined && !Array.isArray(rawTools)) {
    throw new LocalCliError({
      code: "ADVISOR_BAD_TOOLS",
      message: `targets.foundry.tools in persona '${args.persona}' must be a list, got ${typeof rawTools}.`,
      hint: `Fix the tools block in personas/${args.persona}/persona.md.`,
    });
  }
  const tools = rawTools as Record<string, unknown>[] | undefined;

  const personaVersion = fm.version === undefined ? null : String(fm.version);

  // Build a values map from fillable-fields defaults, then let caller overrides win.
  const fillableFields = fm["fillable-fields"] ?? [];
  const valuesMap: Record<string, string> = {};
  for (const field of fillableFields) {
    if (field.default !== undefined) valuesMap[field.name] = field.default;
  }
  for (const [k, v] of Object.entries(args.fieldOverrides ?? {})) valuesMap[k] = v;

  const instructions = renderPersonaBody(personaPath, valuesMap);

  return createPromptVersion({
    credential: args.credential,
    endpoint: args.endpoint,
    name: args.agentName,
    model,
    instructions,
    reasoningEffort,
    ...(tools ? { tools } : {}),
    metadata: {
      persona: args.persona,
      personaVersion,
      fillableFieldValues: JSON.stringify(valuesMap),
      // Derived here, once, from the agent NAME — so no call site can forget it
      // and no two call sites can disagree.
      productVisibility: productVisibilityFor(args.agentName),
    },
    ...(args.onNotice ? { onNotice: args.onNotice } : {}),
  });
}
